import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Alert,
  Skeleton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GroupsIcon from '@mui/icons-material/Groups';
import SettingsIcon from '@mui/icons-material/Settings';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { teamService, Team } from '@services/teamService';
import { toDisplayError } from '../../utils/apiError';

const createTeamSchema = z.object({
  name: z.string().min(1, 'teams.validation.nameRequired').max(255, 'teams.validation.nameTooLong'),
  slug: z
    .string()
    .min(1, 'teams.validation.slugRequired')
    .max(100, 'teams.validation.slugTooLong')
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'teams.validation.slugFormat'),
  description: z.string().optional(),
});

type CreateTeamFormData = z.infer<typeof createTeamSchema>;

interface TeamsTabProps {
  orgId: string;
  orgSlug: string;
}

function TeamsTab({ orgId, orgSlug }: TeamsTabProps) {
  const { t } = useTranslation();

  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CreateTeamFormData>({
    resolver: zodResolver(createTeamSchema),
    defaultValues: {
      name: '',
      slug: '',
      description: '',
    },
  });

  const watchName = watch('name');
  const watchSlug = watch('slug');

  // Auto-generate slug from name (only if user hasn't manually edited the slug)
  useEffect(() => {
    if (watchName && !slugManuallyEdited) {
      const generatedSlug = watchName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setValue('slug', generatedSlug);
    }
  }, [watchName, slugManuallyEdited, setValue]);

  // Check slug availability with debounce
  useEffect(() => {
    if (!watchSlug || watchSlug.length < 1) {
      setSlugAvailable(null);
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingSlug(true);
      try {
        const result = await teamService.checkSlug(orgId, watchSlug);
        setSlugAvailable(result.available);
      } catch {
        setSlugAvailable(null);
      } finally {
        setCheckingSlug(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [watchSlug, orgId]);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await teamService.list(orgId);
      setTeams(result.items ?? []);
    } catch (err) {
      setError(t('teams.errors.fetchFailed'));
      console.error('Failed to fetch teams:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  useEffect(() => {
    void fetchTeams();
  }, [fetchTeams]);

  const handleCreateTeam = async (data: CreateTeamFormData) => {
    setCreating(true);
    setCreateError(null);
    try {
      await teamService.create(orgId, {
        name: data.name,
        slug: data.slug,
        description: data.description || undefined,
      });
      setCreateDialogOpen(false);
      reset();
      await fetchTeams();
    } catch (err) {
      setCreateError(toDisplayError(err, t('teams.errors.createFailed')));
    } finally {
      setCreating(false);
    }
  };

  const handleOpenCreateDialog = () => {
    reset();
    setSlugAvailable(null);
    setCreateError(null);
    setSlugManuallyEdited(false);
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    reset();
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="text" width={100} height={32} />
          <Skeleton variant="rectangular" width={120} height={36} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="circular" width={48} height={48} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="40%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">{t('teams.title')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
          {t('teams.createNew')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {teams.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <GroupsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('teams.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('teams.empty.description')}
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
              {t('teams.createFirst')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {teams.map((team) => (
            <Grid item xs={12} sm={6} md={4} key={team.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: 'secondary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                      }}
                    >
                      <GroupsIcon sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" noWrap>
                        {team.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        /{team.slug}
                      </Typography>
                    </Box>
                  </Box>
                  {team.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {team.description}
                    </Typography>
                  )}
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Chip
                      label={t(`teams.statuses.${team.status}`)}
                      size="small"
                      color={team.status === 'active' ? 'success' : 'default'}
                    />
                    {team.member_count !== undefined && (
                      <Chip
                        label={`${team.member_count} members`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  {/* REV-732: goes to a URL, so it is a link. */}
                  <Button
                    size="small"
                    startIcon={<SettingsIcon />}
                    component={RouterLink}
                    to={`/orgs/${orgSlug}/teams/${team.slug}`}
                  >
                    {t('teams.manage')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Team Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(handleCreateTeam)}>
          <DialogTitle>{t('teams.create.title')}</DialogTitle>
          <DialogContent>
            {createError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createError}
              </Alert>
            )}
            <TextField
              {...register('name')}
              label={t('teams.fields.name')}
              fullWidth
              margin="normal"
              error={!!errors.name}
              helperText={errors.name ? t(errors.name.message as string) : undefined}
              disabled={creating}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              {...register('slug', {
                onChange: () => setSlugManuallyEdited(true),
              })}
              label={t('teams.fields.slug')}
              fullWidth
              margin="normal"
              error={!!errors.slug || slugAvailable === false}
              helperText={
                errors.slug
                  ? t(errors.slug.message as string)
                  : slugAvailable === false
                    ? t('teams.validation.slugTaken')
                    : slugAvailable === true
                      ? t('teams.validation.slugAvailable')
                      : t('teams.fields.slugHint')
              }
              disabled={creating}
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  endAdornment: checkingSlug ? <CircularProgress size={20} /> : undefined,
                },
              }}
            />
            <TextField
              {...register('description')}
              label={t('teams.fields.description')}
              fullWidth
              margin="normal"
              multiline
              rows={3}
              error={!!errors.description}
              helperText={errors.description ? t(errors.description.message as string) : undefined}
              disabled={creating}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseCreateDialog} disabled={creating}>
              {t('common.cancel')}
            </Button>
            {/*
              REV-918: disabled only while the request is in flight, never on
              validity (CLAUDE.md, never pre-disable submit). A taken slug is
              said in the field above, and submitting it anyway gets the API's
              own refusal, shown in this dialog.
            */}
            <Button type="submit" variant="contained" disabled={creating}>
              {creating ? <CircularProgress size={24} color="inherit" /> : t('teams.create.submit')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default TeamsTab;
